import { Box, InputBase, type InputBaseProps } from "@mui/material";
import Autocomplete, { autocompleteClasses, type AutocompleteInputChangeReason } from "@mui/material/Autocomplete";
import SearchIcon from "@mui/icons-material/Search";
import type { SxProps, Theme } from "@mui/material/styles";
import { setRef } from "@mui/material/utils";
import { useCallback, useState } from "react";

const hoverSelector = "&:hover" as const;
const focusWithinSelector = "&:focus-within" as const;
const inputBaseInputSelector = "& .MuiInputBase-input" as const;
const inputPlaceholderSelector = "&::placeholder" as const;
const ICON_GUTTER = 12;
const ICON_SIZE = 16;
const GAP_ICON_TEXT = 8;
const INPUT_PAD_LEFT = ICON_GUTTER + ICON_SIZE + GAP_ICON_TEXT;
const SUGGESTION_LISTBOX_MAX_HEIGHT_PX = 36 * 3 + 8;

const asSxArray = (x: SxProps<Theme> | undefined): Array<SxProps<Theme>> => {
  if (x === undefined) {
    return [];
  }

  if (Array.isArray(x)) {
    return x as Array<SxProps<Theme>>;
  }

  return [x];
};

export interface SearchBoxProps {
  readonly placeholder?: string;
  readonly inputRef?: InputBaseProps["inputRef"];
  readonly inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
  readonly onBlur?: React.FocusEventHandler<HTMLInputElement>;
  readonly value?: string;
  readonly onChange?: (value: string) => void;
  readonly onClear?: () => void;
  readonly disabled?: boolean;
  readonly height?: number;
  readonly borderRadius?: number | string;
  readonly sx?: SxProps<Theme>;
  readonly inputSx?: SxProps<Theme>;
  readonly suggestions?: readonly string[];
}

const SearchBox = ({
  placeholder,
  inputRef,
  inputProps,
  onBlur: onBlurProp,
  value,
  onChange,
  onClear,
  disabled,
  height = 40,
  borderRadius: borderRadiusProp,
  sx,
  inputSx,
  suggestions = []
}: SearchBoxProps) => {
  let resolvedBorderRadius: string;
  if (borderRadiusProp === undefined) {
    resolvedBorderRadius = "1000px";
  } else if (typeof borderRadiusProp === "number") {
    resolvedBorderRadius = `${borderRadiusProp}px`;
  } else {
    resolvedBorderRadius = borderRadiusProp;
  }

  const isControlled = value !== undefined;
  const [internalValue, setInternalValue] = useState("");
  const inputValue = isControlled ? (value ?? "") : internalValue;

  const resolvedDisabled = disabled ?? inputProps?.disabled ?? false;

  const { onBlur: inputOnBlur, ...inputPropsRest } = inputProps ?? {};
  const mergedInputProps: React.InputHTMLAttributes<HTMLInputElement> = {
    ...inputPropsRest,
    "aria-label": inputProps?.["aria-label"] ?? "search"
  };

  const handleInputChange = useCallback(
    (_event: React.SyntheticEvent, next: string, reason: AutocompleteInputChangeReason) => {
      if (!isControlled) {
        setInternalValue(next);
      }

      if (reason === "clear") {
        onClear?.();
      }

      onChange?.(next);
    },
    [isControlled, onChange, onClear]
  );

  return (
    <Box
      sx={
        [
          (theme) => ({
            position: "relative",
            width: "100%",
            border: "none",
            backgroundColor: theme.palette.action.disabledBackground,
            boxShadow: theme.shadows[2],
            [hoverSelector]: {
              backgroundColor: theme.palette.action.selected
            },
            [focusWithinSelector]: {
              backgroundColor: theme.palette.action.disabledBackground,
              boxShadow: theme.shadows[2]
            }
          }),
          {
            borderRadius: resolvedBorderRadius,
            height
          },
          ...asSxArray(sx)
        ] as SxProps<Theme>
      }
    >
      <Box
        sx={{
          position: "absolute",
          left: 0,
          top: 0,
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          paddingLeft: `${ICON_GUTTER}px`,
          width: `${ICON_GUTTER + ICON_SIZE}px`,
          pointerEvents: "none",
          color: "text.disabled"
        }}
      >
        <SearchIcon sx={{ fontSize: ICON_SIZE }} />
      </Box>
      <Autocomplete
        freeSolo
        fullWidth
        size="small"
        options={[...suggestions]}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        disabled={resolvedDisabled}
        selectOnFocus={false}
        clearOnBlur={false}
        handleHomeEndKeys
        getOptionLabel={(o) => o}
        slotProps={{
          popupIndicator: { sx: { display: "none" } },
          clearIndicator: { sx: { color: "text.disabled" } },
          listbox: {
            sx: {
              maxHeight: SUGGESTION_LISTBOX_MAX_HEIGHT_PX,
              py: 0.5,
              fontSize: 14,
              overflowY: "auto",
              overflowX: "hidden"
            }
          },
          popper: { sx: { zIndex: (t) => t.zIndex.modal + 2 } }
        }}
        sx={{
          width: "100%",
          height: "100%",
          position: "relative",
          [`& .${autocompleteClasses.endAdornment}`]: {
            right: `${ICON_GUTTER}px`
          }
        }}
        renderInput={(params) => {
          const handleMouseDown = params.slotProps.input.onMouseDown;

          return (
            <InputBase
              id={params.id}
              ref={params.slotProps.input.ref}
              className={params.slotProps.input.className}
              disabled={params.disabled}
              fullWidth={params.fullWidth}
              startAdornment={params.slotProps.input.startAdornment}
              endAdornment={params.slotProps.input.endAdornment}
              onMouseDown={handleMouseDown}
              placeholder={placeholder}
              inputProps={{
                ...params.slotProps.htmlInput,
                ...mergedInputProps,
                ref: (el: HTMLInputElement | null) => {
                  setRef(params.slotProps.htmlInput.ref, el);
                  setRef(inputRef, el);
                },
                onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
                  params.slotProps.htmlInput.onBlur?.(e);
                  inputOnBlur?.(e);
                  onBlurProp?.(e);
                }
              }}
              sx={
                [
                  (theme) => ({
                    color: "inherit",
                    width: "100%",
                    height: "100%",
                    [inputBaseInputSelector]: {
                      padding: 0,
                      paddingLeft: `${INPUT_PAD_LEFT}px`,
                      paddingRight: `${ICON_GUTTER}px`,
                      width: "100%",
                      fontSize: 14,
                      lineHeight: 1.43,
                      letterSpacing: "0.17px",
                      [inputPlaceholderSelector]: {
                        color: theme.palette.text.disabled,
                        opacity: 1
                      }
                    }
                  }),
                  ...asSxArray(inputSx)
                ] as SxProps<Theme>
              }
            />
          );
        }}
      />
    </Box>
  );
};

export default SearchBox;
